'use client'

import type { JSX } from "react";
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  getAnalyticsConsent,
  OPEN_COOKIE_SETTINGS_EVENT,
  setAnalyticsConsent,
  type AnalyticsConsent,
} from '@/lib/analytics'

export { openCookieSettings } from '@/lib/analytics'

export function CookieConsentBanner(): JSX.Element | null {
  const [choice, setChoice] = useState<AnalyticsConsent | null>(null)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const stored = getAnalyticsConsent()
    setChoice(stored)
    setOpen(stored === null)

    const openSettings = (): void => setOpen(true)
    window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, openSettings)
    return () => window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, openSettings)
  }, [])

  const choose = (consent: AnalyticsConsent): void => {
    setAnalyticsConsent(consent)
    setChoice(consent)
    setOpen(false)
  }

  if (!open) return null

  return (
    <div
      role="dialog"
      aria-labelledby="cookie-consent-title"
      aria-describedby="cookie-consent-description"
      className="fixed inset-x-0 bottom-0 z-[60] border-t border-gray-200 bg-white/95 px-4 py-4 shadow-[0_-12px_40px_rgba(0,0,0,0.12)] backdrop-blur-sm sm:bottom-5 sm:left-auto sm:right-5 sm:max-w-md sm:rounded-xl sm:border"
    >
      <p
        id="cookie-consent-title"
        className="text-xs font-semibold uppercase tracking-wide text-indigo-600"
      >
        Cookie preferences
      </p>
      <p
        id="cookie-consent-description"
        className="mt-2 text-sm leading-relaxed text-gray-600"
      >
        We use analytics cookies to understand how the site is used and improve
        content. You can accept analytics or continue with essential cookies only.
      </p>
      <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end">
        <Button
          type="button"
          variant="outline"
          className="w-full sm:w-auto"
          onClick={() => choose('declined')}
        >
          Essential only
        </Button>
        <Button
          type="button"
          className="w-full bg-indigo-600 hover:bg-indigo-700 sm:w-auto"
          onClick={() => choose('accepted')}
        >
          Accept analytics
        </Button>
      </div>
      {choice ? (
        <p className="mt-3 text-xs text-gray-500">
          Current choice:{' '}
          {choice === 'accepted' ? 'Analytics accepted' : 'Essential only'}.
        </p>
      ) : null}
    </div>
  )
}
